/**
 * Anchoring a comment in its chapter (decision 58; sync case table C7; part 23c log, table AN): its quote and the context
 * each side found in the chapter's visible text, white space compared as one space, so the record holds the range it
 * covers there and the quote and context as the writer sees them. A comment whose quote isn't found keeps what its source
 * gave, with no range, for the viewing device to re-anchor (part 39). Pure.
 */
import { visibleBlocks } from '../literal/visible.ts';
import { parseChapter } from '../parser/parse.ts';

/** AN6: the longest quote a comment keeps, in UTF-16 units. */
export const MAX_QUOTE = 2_000;

/** AN6 (decision 58: "about 30 characters"): the context kept each side, in UTF-16 units. */
export const CONTEXT = 30;

/** AN8: the most characters the anchoring of one re-import scans in all. */
export const MAX_SCAN = 64 * 1024 * 1024;

/** What a source says of a comment's place: its quote and the text before and after it, raw. */
export interface CommentProbe {
  readonly quote: string;
  readonly before: string;
  readonly after: string;
}

/** A comment anchored (AN3–AN6): its range in the visible text (null: not found), its quote and its context. */
export interface CommentAnchor {
  readonly range: { readonly from: number; readonly to: number } | null;
  readonly quote: string;
  readonly before: string;
  readonly after: string;
}

/** AN1: a chapter's visible text: part 04's visible blocks, joined by line ends. */
export function chapterVisibleText(text: string): string {
  return visibleBlocks(parseChapter(text))
    .map((block) => block.text)
    .join('\n');
}

/** AN8: what is left of the scanning one re-import may do. */
export class ScanBudget {
  #left: number;

  constructor(total: number = MAX_SCAN) {
    this.#left = total;
  }

  /** Takes `count` characters, if that many are left. */
  take(count: number): boolean {
    if (count > this.#left) return false;
    this.#left -= count;
    return true;
  }
}

/**
 * AN2: whether the UTF-16 unit `unit` is white space as `\s` has it (every such character is one unit, and no half of a
 * pair is one), read without a regular expression: the 30k-word chapter's text is read a unit at a time (the notes bench).
 */
export function isSpace(unit: number): boolean {
  if (unit === 0x20 || (unit >= 0x09 && unit <= 0x0d)) return true;
  if (unit < 0xa0) return false;
  return unit === 0xa0 || unit === 0x1680 || (unit >= 0x2000 && unit <= 0x200a) || unit === 0x2028 || unit === 0x2029 || unit === 0x202f || unit === 0x205f || unit === 0x3000 || unit === 0xfeff;
}

/**
 * AN2: `parts` joined, each white space run (across parts too) one space, with where each part starts in the result
 * (a run across a boundary belongs before it) and, with `map`, the index in the joined text of each result character.
 * Part 19e's finding of notes (`notes/locate.ts`) compares the same way.
 */
export function collapse(parts: readonly string[], map?: number[]): { readonly text: string; readonly starts: readonly number[] } {
  let text = '';
  const starts: number[] = [];
  let index = 0;
  let space = false;
  for (const part of parts) {
    starts.push(text.length);
    // The units since the last white space, copied in one slice when the next comes or the part ends.
    let kept = 0;
    for (let unit = 0; unit < part.length; unit += 1) {
      if (!isSpace(part.charCodeAt(unit))) {
        map?.push(index + unit);
        space = false;
        continue;
      }
      text += part.slice(kept, unit);
      kept = unit + 1;
      if (!space) {
        text += ' ';
        map?.push(index + unit);
      }
      space = true;
    }
    text += part.slice(kept);
    index += part.length;
  }
  return { text, starts };
}

/** AN6: `text` without a surrogate half at its start or end, left by a cut. */
export function whole(text: string): string {
  return text.replace(/^[\uDC00-\uDFFF]/u, '').replace(/[\uD800-\uDBFF]$/u, '');
}

/**
 * Table AN over one chapter's visible text: each probe anchored in turn, the next searched from where the last was found
 * (AN7), within a {@link ScanBudget} the whole re-import shares (AN8).
 */
export class ChapterAnchors {
  readonly #visible: string;
  /** AN2: the visible text with white space as one space, and each of its units' index in the visible text. */
  readonly #text: string;
  readonly #map: number[] = [];
  readonly #budget: ScanBudget;
  /** AN7: where the last comment was found, in {@link #text}. */
  #hint = 0;

  constructor(visible: string, budget: ScanBudget) {
    this.#visible = visible;
    this.#text = collapse([visible], this.#map).text;
    this.#budget = budget;
  }

  /** AN3: where `needle` is in the text, from the hint, else from the start, within the budget; -1 when not. */
  #find(needle: string): number {
    for (const from of this.#hint > 0 ? [this.#hint, 0] : [0]) {
      if (!this.#budget.take(this.#text.length - from)) return -1;
      const found = this.#text.indexOf(needle, from);
      if (found >= 0) return found;
    }
    return -1;
  }

  /** The visible index of the text's unit `at`, or the visible text's end past the last. */
  #at(at: number): number {
    return this.#map[at] ?? this.#visible.length;
  }

  /** AN3–AN7: `probe` anchored. */
  anchor(probe: CommentProbe): CommentAnchor {
    const before = collapse([probe.before]).text.slice(-CONTEXT);
    const after = collapse([probe.after]).text.slice(0, CONTEXT);
    const spaced = collapse([probe.quote]).text;
    const quote = spaced.trim();
    const found = quote === '' ? this.#point(before, after) : this.#quote(before, spaced, after);
    const range = found === null ? null : this.#whole(found);
    if (range === null) return { range: null, quote: whole(quote.slice(0, MAX_QUOTE)), before: whole(before), after: whole(after) };
    return {
      range,
      quote: whole(this.#visible.slice(range.from, Math.min(range.to, range.from + MAX_QUOTE))),
      before: whole(this.#visible.slice(Math.max(0, range.from - CONTEXT), range.from)),
      after: whole(this.#visible.slice(range.to, range.to + CONTEXT)),
    };
  }

  /** AN6: `range` widened to whole characters: never between the halves of a surrogate pair. */
  #whole(range: { from: number; to: number }): { from: number; to: number } {
    const text = this.#visible;
    const low = (at: number): boolean => /[\uDC00-\uDFFF]/u.test(text.charAt(at)) && /[\uD800-\uDBFF]/u.test(text.charAt(at - 1));
    const from = low(range.from) ? range.from - 1 : range.from;
    return { from, to: Math.max(from, low(range.to) ? range.to + 1 : range.to) };
  }

  /** AN3: a quote's range: context, quote and context found together, else the quote alone (`spaced`, trimmed). */
  #quote(before: string, spaced: string, after: string): { from: number; to: number } | null {
    const quote = spaced.trim();
    const joined = collapse([before, spaced, after]);
    // The quote's place in the joined probe, a space at its edge (its own, or a run from the context before) left out.
    let start = Number(joined.starts[1]);
    if (joined.text[start] === ' ') start += 1;
    let at = this.#find(joined.text);
    if (at < 0) {
      start = 0;
      at = this.#find(quote);
      if (at < 0) return null;
    }
    const from = at + start;
    this.#hint = from;
    return { from: this.#at(from), to: this.#at(from + quote.length - 1) + 1 };
  }

  /** AN4: a point: between both contexts found together, else after the one before, else before the one after. */
  #point(before: string, after: string): { from: number; to: number } | null {
    const joined = collapse([before, after]);
    const tries: (readonly [string, number])[] = [];
    if (joined.text.trim() !== '') tries.push([joined.text, Number(joined.starts[1])]);
    if (before.trim() !== '') tries.push([before, before.length]);
    if (after.trim() !== '') tries.push([after, 0]);
    // AN4: a chapter with no text around the comment: its start.
    if (tries.length === 0) return { from: 0, to: 0 };
    for (const [needle, offset] of tries) {
      const at = this.#find(needle);
      if (at < 0) continue;
      this.#hint = at + offset;
      const point = this.#at(at + offset);
      return { from: point, to: point };
    }
    return null;
  }
}
